import {
  Course,
  CourseProgress,
  CurrentCourseProgress,
  UserProgress,
} from "../types/types"; // Adjust the import path
import { userApi } from "../store/api/userApi";
import { AppDispatch } from "../store/store";

export function findCourseProgress(
  courses: CourseProgress[] | undefined,
  courseId: string
): CourseProgress | undefined {
  return courses?.find((progress) => progress.courseId === courseId);
}

export function isLessonCompleted(
  courseProgress: CourseProgress | undefined,
  lessonId: string
): boolean {
  return (
    courseProgress?.lessonProgress.some((l) => l.lessonId === lessonId) ?? false
  );
}

export function getLessonsCompleted(
  userProgress: CourseProgress[] | undefined,
  courseId: string
): number {
  const courseProgress = findCourseProgress(userProgress, courseId);
  return courseProgress?.lessonProgress?.length || 0;
}

export const invalidateUserTags = (dispatch: AppDispatch, userId: string) => {
  dispatch(userApi.util.invalidateTags([{ type: "Users", id: userId }]));
};

export function getAllCoursesWithProgress(
  allCourses: Course[],
  completedCourses: CourseProgress[]
): {
  completedCourses: CurrentCourseProgress[];
  incompleteCourses: CurrentCourseProgress[];
  notStartedCourses: Course[];
} {
  const completedCourseData: CurrentCourseProgress[] = [];
  const incompleteCourseData: CurrentCourseProgress[] = [];
  const notStartedCourses: Course[] = [];
  let fullyCompletedCount = 0;

  // Process all courses
  allCourses.forEach((course) => {
    const courseProgress = completedCourses.find(
      (c) => c.courseId === course.id
    );

    if (courseProgress) {
      // Course is in progress or completed
      const lessonsTotal = course.lessons.length;
      const lessonsCompleted = getLessonsCompleted(completedCourses, course.id);
      const percentComplete = Math.floor(
        (lessonsCompleted / lessonsTotal) * 100
      );
      if (percentComplete === 100) {
        fullyCompletedCount++;
        completedCourseData.push({
          course: course,
          percentageComplete: percentComplete,
        });
      } else if (percentComplete > 0) {
        incompleteCourseData.push({
          course: course,
          percentageComplete: percentComplete,
        });
      } else {
        notStartedCourses.push(course);
      }
    } else {
      // Course not started yet
      notStartedCourses.push(course);
    }
  });

  // If all courses are fully completed, return all as completed
  if (fullyCompletedCount === allCourses.length) {
    return {
      completedCourses: completedCourseData,
      incompleteCourses: [],
      notStartedCourses: [],
    };
  }

  // Sort completed and incomplete courses by completion percentage (descending)
  completedCourseData.sort(
    (a, b) => b.percentageComplete - a.percentageComplete
  );
  incompleteCourseData.sort(
    (a, b) => b.percentageComplete - a.percentageComplete
  );

  return {
    completedCourses: completedCourseData,
    incompleteCourses: incompleteCourseData,
    notStartedCourses: notStartedCourses,
  };
}

export function getCoursesInProgress(
  courses: Course[],
  userProgress?: UserProgress
): CurrentCourseProgress[] {
  const coursesInProgress: CurrentCourseProgress[] = [];

  if (
    !userProgress ||
    userProgress.courses.length === 0 ||
    !courses ||
    courses.length === 0
  ) {
    return [];
  }

  for (let i = 0; i < courses.length; i++) {
    const userCourse = userProgress.courses.find(
      (c) => c.courseId === courses[i].id
    );
    if (userCourse) {
      const percentComplete = Math.floor(
        (userCourse.lessonProgress.length / courses[i].lessons.length) * 100
      );
      const n = { course: courses[i], percentageComplete: percentComplete };
      coursesInProgress.push(n);
    }
  }
  return coursesInProgress;
}

/**
 * Calculate time left for a course based on progress percentage and total duration
 * @param progressPercentage - Current progress percentage (0-100)
 * @param estimatedTotalDurationMinutes - Total estimated duration in minutes
 * @returns Formatted time left string (e.g., "2 hours left")
 */
export function calculateTimeLeft(
  progressPercentage: number,
  estimatedTotalDurationMinutes: number
): string {
  const remainingPercentage = 100 - progressPercentage;
  const remainingMinutes =
    (remainingPercentage / 100) * estimatedTotalDurationMinutes;
  const timeLeftHours = Math.max(1, Math.ceil(remainingMinutes / 60));
  return `${timeLeftHours} hours left`;
}

export function minutesToHoursAndMinutes(totalMinutes: number): string {
  if (!totalMinutes) return ""; // handles null, undefined, 0, NaN

  if (totalMinutes < 60) {
    const m = Math.max(1, Math.round(totalMinutes));
    return `${m} ${m === 1 ? "minute" : "minutes"}`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const hoursPart = hours === 1 ? "1 hour" : `${hours} hours`;
  const minutesPart = minutes > 0 ? ` ${minutes} ${minutes === 1 ? "minute" : "minutes"}` : "";

  return `${hoursPart}${minutesPart}`.trim();
}

export function formatTimeLeft(
  progressPercentage: number,
  estimatedDurationMinutes: number,
  isCompleted: boolean,
): string {
  if (isCompleted) {
    return "Completed";
  }

  if (!estimatedDurationMinutes || estimatedDurationMinutes === 0) {
    return progressPercentage > 0 ? "In progress" : "Not started";
  }

  const remainingMinutes = Math.round(
    estimatedDurationMinutes * (1 - progressPercentage / 100),
  );

  return formatRemainingMinutesLeft(remainingMinutes);
}

export function formatRemainingMinutesLeft(remainingMinutes: number): string {
  if (remainingMinutes > 60) {
    const hours = Math.floor(remainingMinutes / 60);
    const minutes = remainingMinutes % 60;
    return minutes > 0 ? `${hours}h ${minutes}m left` : `${hours}h left`;
  }

  return `${remainingMinutes}m left`;
}
